import React, { FC, useMemo, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { SPACING } from '@/shared/styles/spacing'
import {
  Yakureki,
  YakurekiDetail,
  isInProgressStatus,
} from '@/shared/types/yakurekiCore'
import { SoapSectionKeys, SoapContent } from '@/shared/types/yakurekiSoap'
import { AudioUploadButton } from '@/apps/legacy/features/yakureki/components/AudioUploadButton'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { soapLabels } from '@/shared/constants/const'
import { useToast } from '@/shared/contexts/ToastContext'
import {
  useUpdateSoapMutation,
  usePatchCopyDoneMutation,
  useSelectionFromYakureki,
} from '@/features/yakureki'
import PatientActionProposal from '@/apps/legacy/features/yakureki/components/PatientActionProposal'
import { SoapUpdateChat } from '@/apps/legacy/components/organisms/SoapUpdateChat'
import CloseIcon from '@mui/icons-material/Close'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PatientDetailDrawer from '@/apps/legacy/components/PatientDetailDrawer'
import { usePatientManagement } from '@/apps/legacy/features/patients/hooks/usePatientManagement'
import { useSoapOperations } from '@/features/formatter'
import { useGuidanceOperations } from '@/apps/legacy/features/guidance/hooks/useGuidanceOperations'
import { SoapContentArea } from '@/apps/legacy/components/organisms/PostRecordingScreen/SoapContentArea'
import { BulkReplaceDialog } from '@/apps/legacy/components/organisms/BulkReplaceDialog'
import { SoapEditHistoryDialog } from '@/apps/legacy/components/organisms/SoapEditHistoryDialog'
import { extractMedicationList } from '@/shared/utils/medicationHelper'
import { LAYOUT_CONSTANTS } from '@/shared/constants/layout'
import { RequestIdDisplay } from '@/apps/legacy/components/molecules/RequestIdDisplay'
import { Copyright } from '@/apps/legacy/components/molecules/Copyright'

export interface PostRecordingScreenContentProps {
  selectedPatient?: Yakureki
  patientList: Yakureki[]
  patientDetail: YakurekiDetail
  renderEditableModal: () => React.ReactNode
  reloadPatientDetail: () => Promise<void>
  onRegenerateSOAP: () => Promise<void>
  /**
   * SOAP 生成中オーバーレイの表示可否。caller (legacy / streaming wrapper) が
   * 自身のコンテキストに応じた条件を評価して渡す。undefined の場合は legacy の
   * 既定条件 (patientList.find.status === 'processing') で判定 (legacy wrapper
   * は overlayVisible を指定せず既定動作に委ねる).
   */
  overlayVisible?: boolean
}

/**
 * 録音後 / SOAP 表示統合画面の本体 JSX。
 *
 * 本 component 自体は mode-agnostic (legacy/streaming 共通)。mode 固有の
 * 関心事 (overlay 条件、mount 時 refetch 等) は caller (legacy/streaming の
 * screen wrapper) 側に押し出す。
 */
export const PostRecordingScreenContent: FC<
  PostRecordingScreenContentProps
> = ({
  selectedPatient,
  patientList,
  patientDetail,
  renderEditableModal,
  reloadPatientDetail,
  onRegenerateSOAP,
  overlayVisible,
}) => {
  const theme = useTheme()
  const { showToast } = useToast()

  // TanStack Query Mutations
  const updateSoapMutation = useUpdateSoapMutation()
  const patchCopyDoneMutation = usePatchCopyDoneMutation()

  const [patientDetailModalOpen, setPatientDetailModalOpen] = useState(false)
  const [chatDialogOpen, setChatDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [bulkReplaceOpen, setBulkReplaceOpen] = useState(false)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)

  // カスタムフックで患者管理ロジックを集約
  const {
    displayPatient,
    patientError,
    actionProposals,
    actionsError,
    handleActionStatusUpdate,
  } = usePatientManagement(patientDetail)

  // カスタムフックでSOAP操作ロジックを集約
  // ADR-055: SelectionContext 関連の props は削除（TanStack Query で直接取得）
  const { syncSelectionToServer, copyToClipboard } = useSoapOperations(
    patientDetail,
    selectedPatient
  )

  // ADR-055: TanStack Query から選択状態を取得
  const { selection } = useSelectionFromYakureki(selectedPatient?.id)

  // カスタムフックで指導支援ロジックを集約
  const { guidanceData, guidanceLoading } = useGuidanceOperations(
    patientDetail?.id
  )

  // SOAP改善提案の実行処理（チャットダイアログを開く）
  const handleSoapImprovementExecute = useCallback(() => {
    setChatDialogOpen(true)
  }, [])

  // SOAP更新処理（改善提案適用時）
  const handleSoapUpdate = useCallback(async () => {
    // patientDetailを更新
    if (patientDetail) {
      // reloadPatientDetailを呼び出して最新のデータを取得
      await reloadPatientDetail()
    }
  }, [patientDetail, reloadPatientDetail])

  // 患者詳細モーダルを開く
  const handleOpenPatientDetail = useCallback(() => {
    setPatientDetailModalOpen(true)
  }, [])

  // 患者詳細モーダルを閉じる
  const handleClosePatientDetail = useCallback(() => {
    setPatientDetailModalOpen(false)
  }, [])

  // 指導支援データのキーフレーズ判定は削除（常に表示）

  // 重複した関数定義を削除（useSoapOperationsフックから提供される）

  // セクション別にコピーする処理
  // ADR-055 対応: TanStack Query から選択状態を取得、マスターデータから content を取得
  const copySectionToClipboard = async (key: SoapSectionKeys) => {
    if (!selection || !patientDetail) return

    const selectedIndices = selection[key]

    // 1. Check if section is empty (no items) but header is selected
    const sectionItems = patientDetail.soaps?.[key] || []
    const headerOnlySelection =
      sectionItems.length === 0 && selectedIndices.size === 0

    // 2. Get content based on selection type
    // ADR-055: selection[key] は Set<number>（インデックスのみ）
    // マスターデータ（sectionItems）から content を取得
    let content = ''
    if (headerOnlySelection) {
      // If only the header is selected (empty section)
      content = soapLabels[key]
    } else {
      // Regular case with selected items
      // インデックスをソートしてマスターデータから content を取得
      const sortedIndices = Array.from(selectedIndices).sort((a, b) => a - b)
      content = sortedIndices
        .map((index) => sectionItems[index]?.content)
        .filter(Boolean)
        .join('\n')
    }

    if (content) {
      await navigator.clipboard.writeText(content)
      showToast('コピーしました', 'info', 2000)

      // サーバーに選択状態を同期
      await syncSelectionToServer()

      if (selectedPatient) {
        await patchCopyDoneMutation.mutateAsync(selectedPatient.id)
      }
    }
  }

  const currentYakureki = patientList.find(
    (yakureki) => yakureki.id === patientDetail.id
  )

  // 医薬品リストを抽出
  const medications = useMemo(
    () => extractMedicationList(patientDetail),
    [patientDetail]
  )

  // 一括置換処理
  const handleBulkReplace = async (searchText: string, replaceText: string) => {
    if (!patientDetail?.soaps) return

    try {
      // 置換処理の実装
      const soapContent = { ...patientDetail.soaps }
      // idプロパティを除外してSoapContentのみを取得
      const { id: _id, ...rest } = soapContent
      const updatedSoaps: SoapContent = rest as SoapContent

      // SOAPセクションのキーのみを取得
      const sectionKeys = Object.keys(updatedSoaps) as SoapSectionKeys[]

      sectionKeys.forEach((key) => {
        const items = updatedSoaps[key]

        // 配列であることを確認
        if (!Array.isArray(items)) {
          return
        }

        // 全ての項目を置換
        updatedSoaps[key] = items.map((item) => ({
          ...item,
          content: item.content.split(searchText).join(replaceText),
        }))
      })

      // APIで更新
      await updateSoapMutation.mutateAsync({
        yakurekiId: patientDetail.id,
        soapData: updatedSoaps,
        editSource: 'bulk_replace',
      })
      await reloadPatientDetail()

      showToast('置換が完了しました', 'success', 2000)
    } catch {
      showToast('置換に失敗しました', 'error', 3000)
    }
  }

  return (
    <>
      {/* SOAP生成中のオーバーレイ（患者メニュー以外の全エリア） */}
      {(() => {
        const shouldShowOverlay =
          overlayVisible ??
          !!(currentYakureki && isInProgressStatus(currentYakureki.status))
        return shouldShowOverlay
      })() && (
        <Box
          sx={{
            position: 'fixed',
            top: LAYOUT_CONSTANTS.headerHeight, // 患者カードヘッダーの下から（marginTopと合わせる）
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(245, 249, 255, 0.8)', // 薄い青背景
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: LAYOUT_CONSTANTS.zIndex.overlay,
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: colorTokens.blue[600],
            }}
          >
            SOAP生成中...
          </Typography>
          <Typography
            sx={{
              color: colorTokens.text.secondary,
            }}
          >
            患者の切り替えは上部メニューから可能です
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          height: `calc(100vh - ${LAYOUT_CONSTANTS.headerHeight}px)`,
          backgroundColor: theme.palette.secondary.light,
          overflow: 'hidden',
          marginTop: `${LAYOUT_CONSTANTS.headerHeight}px`,
          [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            height: 'auto',
            minHeight: `calc(100vh - ${LAYOUT_CONSTANTS.headerHeight}px)`,
            overflow: 'auto',
          },
        }}
      >
        <SoapContentArea
          patientDetail={patientDetail}
          isEditMode={isEditMode}
          medications={medications}
          onCopyClick={copyToClipboard}
          onEditModeToggle={() => setIsEditMode(!isEditMode)}
          onBulkReplaceClick={() => setBulkReplaceOpen(true)}
          onRegenerateClick={onRegenerateSOAP}
          onAIChatClick={() => setChatDialogOpen(true)}
          onHistoryClick={() => setHistoryDialogOpen(true)}
          copySectionToClipboard={copySectionToClipboard}
          reloadPatientDetail={reloadPatientDetail}
        />

        {/* Fixed position elements */}
        {localStorageWrapper.getItem('IsAudioUpload') && <AudioUploadButton />}

        {/* Modals */}
        {renderEditableModal()}

        {/* Right column - Action Proposals (全体) */}
        <Box
          sx={{
            width: '50%',
            [theme.breakpoints.down('md')]: {
              width: '100%',
              minHeight: '50vh',
              height: 'auto',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: colorTokens.grey[100],
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Main content area */}
          <Box
            sx={{
              flex: '1 1 auto',
              pl: 1.5,
              pr: SPACING.card,
              py: SPACING.mainContent,
              [theme.breakpoints.down('sm')]: {
                pl: SPACING.mainContent,
                pr: SPACING.mainContent,
                py: SPACING.mainContent,
              },
            }}
          >
            {/* PatientActionProposal - 右側全体に拡大 */}
            <PatientActionProposal
              hasFollowupItems={false}
              onRegisterFollowup={() => {
                // フォローアップ事項を実施予定として登録
                // この機能は現在未実装（将来的な機能として予約）
              }}
              dictatedData={
                patientDetail?.dictated?.conversation_segments || []
              }
              audioUrl={patientDetail?.audio?.audio}
              guidanceData={guidanceData}
              guidanceLoading={guidanceLoading}
              actions={actionProposals}
              onStatusUpdate={handleActionStatusUpdate}
              onOpenPatientDetail={handleOpenPatientDetail}
              onSoapImprovementExecute={handleSoapImprovementExecute}
              yakurekiId={selectedPatient?.id}
              onSoapUpdate={handleSoapUpdate}
              patientDetail={patientDetail}
              sx={{
                height: '100%',
                [theme.breakpoints.down('md')]: {
                  height: 'auto',
                  minHeight: '500px',
                },
              }}
            />
            {(actionsError || patientError) && (
              <Box
                sx={{
                  backgroundColor: 'error.light',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: 2,
                  p: 2,
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: 'error.dark',
                  }}
                >
                  {actionsError?.message || patientError?.message}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Request ID and Copyright */}
          <Box
            sx={{
              px: SPACING.card,
              py: 1,
              position: 'relative',
              zIndex: LAYOUT_CONSTANTS.zIndex.overlay + 1,
            }}
          >
            {patientDetail?.id && (
              <RequestIdDisplay
                requestId={patientDetail.id}
                onCopy={async () => {
                  await navigator.clipboard.writeText(patientDetail.id)
                  showToast('リクエストIDをコピーしました', 'info', 2000)
                }}
              />
            )}
            <Copyright />
          </Box>

          {/* Footer area - 左側パネルと同様のスタイル */}
          <Box
            sx={{
              flexShrink: 0,
              height: 20,
              borderTop: `1px solid ${colorTokens.border.light}`,
              backgroundColor: colorTokens.background.subtle,
            }}
          />
        </Box>
      </Box>

      {/* 患者詳細モーダル */}
      <Dialog
        open={patientDetailModalOpen}
        onClose={handleClosePatientDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">患者詳細</Typography>
          <IconButton onClick={handleClosePatientDetail}>
            <CloseIcon />
          </IconButton>
        </Box>
        <PatientDetailDrawer
          patient={displayPatient}
          onClose={handleClosePatientDetail}
        />
      </Dialog>

      {/* SOAP更新ダイアログ */}
      <Dialog
        open={chatDialogOpen}
        onClose={() => setChatDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            height: '70vh',
            maxHeight: 600,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            backgroundColor: colorTokens.blue[600],
            color: colorTokens.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon sx={{ fontSize: 20 }} />
            <Typography>SOAP更新</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setChatDialogOpen(false)}
            sx={{ color: colorTokens.background.paper }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <SoapUpdateChat
            yakurekiId={patientDetail?.id}
            onSoapUpdate={async () => {
              await reloadPatientDetail()
              setTimeout(() => reloadPatientDetail(), 1500)
            }}
            isLoading={false}
            sx={{
              height: '100%',
              width: '100%',
            }}
          />
        </DialogContent>
      </Dialog>

      {/* 一括置換ダイアログ */}
      <BulkReplaceDialog
        open={bulkReplaceOpen}
        onClose={() => setBulkReplaceOpen(false)}
        soapContent={patientDetail?.soaps || undefined}
        medications={medications}
        onReplace={handleBulkReplace}
      />

      {/* 編集履歴ダイアログ */}
      <SoapEditHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        yakurekiId={patientDetail?.id}
      />
    </>
  )
}
